import { RefObject, useCallback, useEffect } from 'react'

import { KEY_VALUES } from '../constants'

interface UseRootCloseParams {
  onRootClose: () => void
  disabled: boolean
  triggerTarget: RefObject<HTMLElement>
  overlayTarget: RefObject<HTMLElement>
}

interface IsInsideParams {
  element: HTMLElement | null
  target: EventTarget | null
}

const isInside = ({ element, target }: IsInsideParams) =>
  Boolean(element && target instanceof Node && element.contains(target))

const isPlainLeftClick = (event: MouseEvent) =>
  event.button === 0 && !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)

export const useRootClose = ({
  onRootClose,
  disabled,
  triggerTarget,
  overlayTarget,
}: UseRootCloseParams) => {
  const handleDocumentKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === KEY_VALUES.ESC) {
        onRootClose()
      }
    },
    [onRootClose],
  )

  const handleDocumentMouseDown = useCallback(
    (event: MouseEvent) => {
      if (isInside({ element: triggerTarget.current, target: event.target })) return
      if (isInside({ element: overlayTarget.current, target: event.target })) return
      if (!isPlainLeftClick(event)) return

      onRootClose()
    },
    [onRootClose, triggerTarget, overlayTarget],
  )

  useEffect(() => {
    const triggerElement = triggerTarget.current

    if (disabled || !triggerElement) return

    const { ownerDocument } = triggerElement
    ownerDocument.addEventListener('mousedown', handleDocumentMouseDown, true)
    ownerDocument.addEventListener('keyup', handleDocumentKeyUp)

    return () => {
      ownerDocument.removeEventListener('mousedown', handleDocumentMouseDown, true)
      ownerDocument.removeEventListener('keyup', handleDocumentKeyUp)
    }
  }, [triggerTarget, disabled, handleDocumentMouseDown, handleDocumentKeyUp])
}
